/**
 * UserSync Service
 *
 * API client for UserSync (OMHAM CSV data).
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface UserSyncItem {
  _id: string;
  sourceId: string;
  namaPemilik?: string;
  provinsi?: string;
  kabupaten?: string;
  pekerjaan?: string;
  callsign?: string;
  tanggalTerbit?: string;
  masaLakuIar?: string;
  masaLakuKta?: string;
  expectedMasaLakuKta?: string;
  nomorIar?: string;
  status?: string;
  keanggotaan?: string;
  nri?: string;
  jenis?: string;
  email?: string;
  upt?: string;
  paymentId?: string;
  grossAmount?: number;
  statusPayment?: string;
  settlementDate?: string;
  isSyncWithSdppi?: boolean;
}

export type UserSyncSortField =
  | "callsign"
  | "namaPemilik"
  | "provinsi"
  | "kabupaten"
  | "email"
  | "sourceId"
  | "status"
  | "settlementDate"
  | "grossAmount";

export interface UserSyncListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: UserSyncSortField;
  sortOrder?: "asc" | "desc";
}

export interface UserSyncListResponse {
  data: UserSyncItem[];
  total: number;
}

class UserSyncService {
  async getUserSyncList(params: UserSyncListParams = {}): Promise<UserSyncListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set("page", String(params.page));
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.search) searchParams.set("search", params.search);
    if (params.sortBy) searchParams.set("sortBy", params.sortBy);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);

    const url = `${apiConfig.baseURL}/api/user-sync${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    const response = await apiFetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || "Failed to fetch user sync list");
    }

    return response.json();
  }

  async getUserSyncById(id: string): Promise<UserSyncItem | null> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/user-sync/${encodeURIComponent(id)}`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || "Failed to fetch user sync");
    }

    const json = await response.json();
    return json.data ?? null;
  }
}

export const userSyncService = new UserSyncService();
